import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import api from "@/utils/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Clock, Briefcase, Users, TrendingUp, TrendingDown, DollarSign, Calendar, CheckCircle, XCircle } from "lucide-react";
import { format, startOfMonth, endOfMonth, subMonths } from "date-fns";
import { de } from "date-fns/locale";

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [stats, setStats] = useState({
    totalProjects: 0,
    activeProjects: 0,
    totalCustomers: 0,
    totalHours: 0,
    billedHours: 0,
    unbilledHours: 0,
    periodIncome: 0,
    periodExpenses: 0,
    runningTimer: null,
  });
  const [loading, setLoading] = useState(true);
  const [periodStart, setPeriodStart] = useState(format(startOfMonth(new Date()), "yyyy-MM-dd"));
  const [periodEnd, setPeriodEnd] = useState(format(endOfMonth(new Date()), "yyyy-MM-dd"));

  useEffect(() => {
    fetchDashboardData();
  }, [periodStart, periodEnd]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [projects, customers, timeEntries, incomes, expenses, activeTimer] = await Promise.all([
        api.get('/projects'),
        api.get('/customers'),
        api.get('/time-entries'),
        api.get('/incomes'),
        api.get('/expenses'),
        api.get('/time-entries/active'),
      ]);

      const start = new Date(periodStart);
      const end = new Date(periodEnd);
      end.setHours(23, 59, 59, 999); // Include full end day

      const activeProjects = projects.data.filter(p => p.status === "active");
      
      // Filter time entries by period
      const periodTimeEntries = timeEntries.data.filter(entry => {
        const entryDate = new Date(entry.start_time);
        return entryDate >= start && entryDate <= end;
      });

      const totalHours = periodTimeEntries.reduce((sum, entry) => sum + (entry.duration_minutes || 0), 0) / 60;
      const billedHours = periodTimeEntries.filter(e => e.invoiced).reduce((sum, entry) => sum + (entry.duration_minutes || 0), 0) / 60;
      const unbilledHours = periodTimeEntries.filter(e => !e.invoiced).reduce((sum, entry) => sum + (entry.duration_minutes || 0), 0) / 60;
      
      const periodIncome = incomes.data
        .filter(i => {
          const date = new Date(i.date);
          return date >= start && date <= end;
        })
        .reduce((sum, i) => sum + i.amount, 0);
      
      const periodExpenses = expenses.data
        .filter(e => {
          const date = new Date(e.date);
          return date >= start && date <= end;
        })
        .reduce((sum, e) => sum + e.amount, 0);
      setStats({
        totalProjects: projects.data.length,
        activeProjects: activeProjects.length,
        totalCustomers: customers.data.length,
        totalHours: Math.round(totalHours * 10) / 10,
        billedHours: Math.round(billedHours * 10) / 10,
        unbilledHours: Math.round(unbilledHours * 10) / 10,
        periodIncome,
        periodExpenses,
        runningTimer: activeTimer.data,
      });
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const setQuickPeriod = (type) => {
    const now = new Date();
    switch(type) {
      case 'this-month':
        setPeriodStart(format(startOfMonth(now), "yyyy-MM-dd"));
        setPeriodEnd(format(endOfMonth(now), "yyyy-MM-dd"));
        break;
      case 'last-month':
        const lastMonth = subMonths(now, 1);
        setPeriodStart(format(startOfMonth(lastMonth), "yyyy-MM-dd"));
        setPeriodEnd(format(endOfMonth(lastMonth), "yyyy-MM-dd"));
        break;
      case 'this-year':
        setPeriodStart(format(new Date(now.getFullYear(), 0, 1), "yyyy-MM-dd"));
        setPeriodEnd(format(new Date(now.getFullYear(), 11, 31), "yyyy-MM-dd"));
        break;
    }
  };

  const statCards = [
    {
      title: t('dashboard.activeProjects'),
      value: `${stats.activeProjects} / ${stats.totalProjects}`,
      icon: Briefcase,
      color: "text-blue-600",
      bg: "bg-blue-50",
    },
    {
      title: t('dashboard.customers'),
      value: stats.totalCustomers,
      icon: Users,
      color: "text-purple-600",
      bg: "bg-purple-50",
    },
    {
      title: t('dashboard.billedHours'),
      value: `${stats.billedHours}h`,
      icon: CheckCircle,
      color: "text-green-600",
      bg: "bg-green-50",
    },
    {
      title: t('dashboard.openHours'),
      value: `${stats.unbilledHours}h`,
      icon: XCircle,
      color: "text-orange-600",
      bg: "bg-orange-50",
    },
    {
      title: t('dashboard.revenue'),
      value: `${stats.periodIncome.toFixed(2)}€`,
      icon: TrendingUp,
      color: "text-green-600",
      bg: "bg-green-50",
    },
    {
      title: "Ausgaben (Periode)",
      value: `${stats.periodExpenses.toFixed(2)}€`,
      icon: TrendingDown,
      color: "text-red-600",
      bg: "bg-red-50",
    },
    {
      title: "Nettogewinn (Periode)",
      value: `${(stats.periodIncome - stats.periodExpenses).toFixed(2)}€`,
      icon: DollarSign,
      color: "text-emerald-600",
      bg: "bg-emerald-50",
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard">
      <div>
        <h1 className="text-4xl font-bold text-gray-900 mb-2">{t('dashboard.title')}</h1>
        <p className="text-lg text-gray-600">
          {t('dashboard.welcomeMessage')}
        </p>
      </div>


      {/* Period Selector */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Calendar className="h-5 w-5 mr-2" />
            Auswertungszeitraum
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <Label>Von</Label>
              <Input
                type="date"
                value={periodStart}
                onChange={(e) => setPeriodStart(e.target.value)}
                data-testid="period-start"
              />
            </div>
            <div>
              <Label>Bis</Label>
              <Input
                type="date"
                value={periodEnd}
                onChange={(e) => setPeriodEnd(e.target.value)}
                data-testid="period-end"
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setQuickPeriod('this-month')}
              data-testid="quick-this-month"
            >
              Dieser Monat
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setQuickPeriod('last-month')}
              data-testid="quick-last-month"
            >
              Letzter Monat
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setQuickPeriod('this-year')}
              data-testid="quick-this-year"
            >
              Dieses Jahr
            </Button>
          </div>
        </CardContent>
      </Card>

      {stats.runningTimer && (
        <Card className="border-emerald-200 bg-emerald-50" data-testid="running-timer-alert">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="h-3 w-3 bg-emerald-600 rounded-full animate-pulse"></div>
                <div>
                  <p className="font-semibold text-emerald-900">Timer läuft</p>
                  <p className="text-sm text-emerald-700">
                    {stats.runningTimer.project_name} - Gestartet um{" "}
                    {format(new Date(stats.runningTimer.start_time), "HH:mm", { locale: de })}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index} className="card-hover" data-testid={`stat-card-${index}`}>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">
                  {stat.title}
                </CardTitle>
                <div className={`p-2 rounded-lg ${stat.bg}`}>
                  <Icon className={`h-5 w-5 ${stat.color}`} />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-gray-900">{stat.value}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Schnellzugriff</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <a
              href="/time-tracking"
              data-testid="quick-access-time"
              className="p-4 border-2 border-gray-200 rounded-lg hover:border-emerald-500 hover:bg-emerald-50 transition-colors"
            >
              <Clock className="h-6 w-6 text-emerald-600 mb-2" />
              <h3 className="font-semibold text-gray-900">Neue Zeiterfassung</h3>
              <p className="text-sm text-gray-600">Timer starten oder Zeit manuell eintragen</p>
            </a>
            <a
              href="/finances"
              data-testid="quick-access-income"
              className="p-4 border-2 border-gray-200 rounded-lg hover:border-emerald-500 hover:bg-emerald-50 transition-colors"
            >
              <TrendingUp className="h-6 w-6 text-green-600 mb-2" />
              <h3 className="font-semibold text-gray-900">Einnahme erfassen</h3>
              <p className="text-sm text-gray-600">Neue Einnahme oder Rechnung hinzufügen</p>
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}